import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

const localDate = () => new Date().toLocaleDateString("en-CA");

// The day after an immersion: the Coach recalls how he felt and suggests the next one.
export default function ImmersionFollowUp() {
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [voice, setVoice] = useState("idle"); // idle | loading | playing
  const audio = useRef(null);
  useEffect(() => { api.get("/routine/immersion-followup", { params: { local_date: localDate() } }).then((r) => setF(r.data.followup)).catch(() => {}); }, []);
  useEffect(() => () => audio.current?.pause(), []);
  if (!f) return null;
  const done = (go) => { audio.current?.pause(); api.post(`/routine/immersion-followup/${f.id}/seen`).catch(() => {}); setF(null); if (go) navigate(f.next.route); };
  const play = async () => {
    if (voice === "playing") { audio.current?.pause(); setVoice("idle"); return; }
    const a = audio.current || new Audio(); audio.current = a;
    a.onended = () => setVoice("idle");
    setVoice("loading");
    a.play().catch(() => {}); // unlock within the tap
    try {
      if (!a.dataset.ready) {
        const { data } = await api.post(`/routine/immersion-followup/${f.id}/audio`, null, { params: { local_date: localDate() } });
        a.src = `${process.env.REACT_APP_BACKEND_URL}${data.audio}`; a.dataset.ready = "1";
      }
      a.currentTime = 0; await a.play(); setVoice("playing");
    } catch { setVoice("idle"); toast.error("Couldn't play the voice just now."); }
  };
  return (
    <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
      className="mb-10 rounded-3xl border border-white/10 bg-card/40 p-5 sm:p-6" data-testid="routine-immersion-followup">
      <p className="text-[10px] uppercase tracking-[0.22em] text-primary">{f.coach} · since your last immersion</p>
      <div className="flex items-start gap-3 mt-2">
        <button onClick={play} disabled={voice === "loading"} aria-label={`Hear ${f.coach}`} data-testid="immersion-followup-play"
          className="shrink-0 w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center hover:bg-primary/25 transition-colors mt-0.5">
          {voice === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : voice === "playing" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <p className="font-serif text-lg sm:text-xl leading-snug" data-testid="immersion-followup-text">{f.text}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-5">
        <Button className="rounded-full" onClick={() => done(true)} data-testid="immersion-followup-go">{f.next.title} · {f.next.subtitle} →</Button>
        <button onClick={() => done(false)} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="immersion-followup-dismiss">Not today</button>
      </div>
    </motion.section>
  );
}
